import { defineStore } from "pinia";

export interface CategoryItem {
  id: string;
  name: string;
  slug: string;
  level?: number;
  titleShort?: string;
  titleDescription?: string;
  seo?: any;
  content?: any;
  metadata?: any;
  children: CategoryItem[];
  parent?: {
    id: string;
    name: string;
    slug: string;
    parent?: { id: string; name: string; slug: string };
  };
}

export const useCategoriesStore = defineStore("categories", () => {
  const categories = ref<CategoryItem[]>([]);
  const total = ref(0);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const categoriesWithChildren = computed(
    () => categories.value.filter((cat) => cat.children.length > 0).length,
  );

  const totalCategories = computed(() => categories.value.length);

  const totalSubCategories = computed(() =>
    categories.value.reduce((sum, cat) => sum + cat.children.length, 0),
  );

  const fetchCategories = async (force = false) => {
    if (categories.value.length > 0 && !force) return;
    try {
      loading.value = true;
      error.value = null;
      const api = useApi();
      const response = await api<any>("/api/categories");
      categories.value = response.categories || [];
      total.value = response.total || 0;
    } catch (err: any) {
      error.value =
        err.data?.message ||
        err.message ||
        "Kategorien konnten nicht geladen werden";
    } finally {
      loading.value = false;
    }
  };

  const getCategoryById = (id: string): CategoryItem | undefined => {
    for (const cat of categories.value) {
      if (cat.id === id) return cat;
      const child = cat.children.find((c) => c.id === id);
      if (child) return child;
    }
    return undefined;
  };

  const getCategoryPath = (cat: CategoryItem): string => {
    if (cat.parent?.slug) {
      if (cat.parent.parent?.slug) {
        return `${cat.parent.parent.slug}/${cat.parent.slug}/${cat.slug}`;
      }
      return `${cat.parent.slug}/${cat.slug}`;
    }
    // For top-level categories from the store (no parent loaded), check hierarchy
    for (const main of categories.value) {
      if (main.id === cat.id) return cat.slug;
      for (const sub of main.children) {
        if (sub.id === cat.id) return `${main.slug}/${cat.slug}`;
        if (sub.children) {
          for (const subsub of sub.children) {
            if (subsub.id === cat.id)
              return `${main.slug}/${sub.slug}/${cat.slug}`;
          }
        }
      }
    }
    return cat.slug;
  };

  return {
    categories,
    total,
    loading,
    error,
    categoriesWithChildren,
    totalCategories,
    totalSubCategories,
    fetchCategories,
    getCategoryById,
    getCategoryPath,
  };
});
